import React, { useState, useCallback } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Button, Fade, IconButton } from '@material-ui/core';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { createStyles } from '@material-ui/styles';

import { FormLocationObjectField } from 'retired/FormLocationObjectField/FormLocationObjectField';
import LocationObject from 'entities/locationObject/model';
import { renderAddress } from 'utils/renderAddress';

import { useSharedWidgetStyles } from './sharedWidgetStyles';

interface LocationInputProps {
  canGoBack?: boolean;
  isString?: boolean;
  onSelect: (value: LocationObject | string) => void;
  onGoBack?: () => void;
}

const useLocationInputStyles = makeStyles((theme: Theme) =>
  createStyles({
    locationContainer: {
      maxWidth: '80%',
      width: '100%',
      minHeight: theme.spacing(5),
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
    },
  })
);

const LocationInput: React.FC<LocationInputProps> = ({
  canGoBack = false,
  isString = true,
  onSelect,
  onGoBack,
}) => {
  const [visible, setVisible] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<LocationObject | null>(null);

  const classes = useLocationInputStyles();
  const sharedClasses = useSharedWidgetStyles();

  const handleGoBack = useCallback(() => {
    if (onGoBack) {
      setVisible(false);
      // Delay going back to match fade out transition
      setTimeout(() => {
        onGoBack();
      }, 300);
    }
  }, [onGoBack]);

  const handleLocationSelect = useCallback(
    (location: LocationObject) => {
      setVisible(false);
      setTimeout(() => {
        onSelect(isString ? renderAddress(location) : location);
      }, 300);
    },
    [onSelect, isString]
  );

  const handleLocationChange = useCallback((locationObject: LocationObject | undefined) => {
    setSelectedLocation(locationObject || null);
  }, []);

  const isSubmitDisabled = !selectedLocation;

  const handleSubmit = useCallback(() => {
    if (selectedLocation) {
      handleLocationSelect(selectedLocation);
    }
  }, [selectedLocation, handleLocationSelect]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey && !isSubmitDisabled) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [isSubmitDisabled, handleSubmit]
  );

  return (
    <Fade in={visible} timeout={300} mountOnEnter unmountOnExit>
      <div className={sharedClasses.rightJustifiedWidgetContainer}>
        {canGoBack && onGoBack && (
          <IconButton
            className={sharedClasses.backButton}
            size="small"
            onClick={handleGoBack}
            aria-label="Go back"
          >
            <ArrowBackIcon className={sharedClasses.backButtonIcon} />
          </IconButton>
        )}

        <div className={classes.locationContainer}>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div className={sharedClasses.inputFieldContainer} onKeyDown={handleKeyDown} role="presentation">
            <FormLocationObjectField
              input={{
                value: selectedLocation || '',
                onChange: handleLocationChange,
                disableUnderline: true,
                sx: {
                  '& .v5-MuiInput-input': {
                    height: '40px',
                    boxSizing: 'border-box',
                    padding: 0,
                  },
                },
              }}
              meta={{}}
              label=""
              fullWidth
              placeholder="Enter address"
              afterSelect={() => {}}
              sx={{
                flex: 1,
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              className={sharedClasses.sendButton}
              disabled={isSubmitDisabled}
            >
              <SendIcon className={sharedClasses.sendIcon} />
            </Button>
          </div>
        </div>
      </div>
    </Fade>
  );
};

export default LocationInput;
